import { FC, useCallback } from 'react';
import dayjs from 'dayjs';
import { CallIntelTypes } from '@frontend/api-call-intel';
import { useTimePeriodDropDownSelector, DatesRangeFilterSelector } from '@frontend/analytics-shared';
import { useTranslation } from '@frontend/i18n';
import { trackingIds } from '../../../../../tracking-ids';
import { CALL_INTEL_TIME_PERIODS, DEFAULT_CALL_INTEL_TIME_PERIOD } from '../../../constants';
import { FilterTraySection } from '../filter-tray-section/filter-tray-section';
import { useTimePeriodFilterData } from './use-time-period-filter-data';

export const TimePeriodFilterTray: FC = () => {
  const { t } = useTranslation('call-intelligence');
  const { filterValues, updateFilterValues } = useTimePeriodFilterData();

  const { TimePeriodDropDownSelector } = useTimePeriodDropDownSelector({
    defaultPeriod: DEFAULT_CALL_INTEL_TIME_PERIOD,
    skipFindingExistingPeriod: true,
    timePeriods: CALL_INTEL_TIME_PERIODS,
    values: {
      endDate: filterValues.endDate || filterValues.endDate,
      selectedPeriod: filterValues.selectedTimePeriodKey,
      startDate: filterValues.startDate || filterValues.startDate,
    },
  });

  const handleDateRangeChange = useCallback(
    ({ endDate, startDate }: { endDate: string; startDate: string }) => {
      const currentStartDate = dayjs(filterValues.startDate);
      const currentEndDate = dayjs(filterValues.endDate);

      const formattedStartDate = dayjs(startDate).startOf('day').toISOString();
      const formattedEndDate = dayjs(endDate).endOf('day').toISOString();

      if (currentStartDate.isSame(dayjs(startDate), 'date') && currentEndDate.isSame(dayjs(endDate), 'date')) {
        return;
      }

      updateFilterValues({
        startDate: formattedStartDate,
        endDate: formattedEndDate,
        selectedTimePeriodKey: null,
      });
    },
    [filterValues.endDate, filterValues.startDate, updateFilterValues]
  );

  return (
    <FilterTraySection label={t('Date Range')} showClear={false}>
      <TimePeriodDropDownSelector
        onChange={({ endDate, startDate, selectedPeriod }) => {
          updateFilterValues({
            endDate,
            startDate,
            selectedTimePeriodKey: selectedPeriod as CallIntelTypes.SelectedTimePeriodKey,
          });
        }}
        trackingIdBase={trackingIds.callIntel.timeSelector}
      />
      <DatesRangeFilterSelector
        endDate={filterValues.endDate}
        onChange={handleDateRangeChange}
        startDate={filterValues.startDate}
      />
    </FilterTraySection>
  );
};
